import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { monospaceStyle } from "../../theme";
import SectionStrip, { SECTION_STRIP_HEIGHT } from "./SectionStrip";

// One section of a detail view, as the view declares it
export interface DetailSection {
  // Unique within the view, e.g. "claims"
  id: string;
  // Its name in the strip and above its content, e.g. "Claims"
  label: string;
  // Its name above its content where that differs from the strip's, e.g.
  // "Capability claims"; the label by default
  heading?: string;
  // Shows the content without a heading, e.g. for an overview; the strip
  // still names the section
  hideHeading?: boolean;
  // The number of entries it holds, where it has one
  count?: number;
  // What the section shows. A section whose content is null, undefined or
  // false is left out, of the view and of the strip.
  content: React.ReactNode;
}

export interface DetailSectionsProps {
  // The sections, in the order shown
  sections: DetailSection[];
  // The element the view scrolls in; the strip stays at its top
  scrollContainerRef: React.RefObject<HTMLElement>;
  // What stays at the top above the strip, such as the view's head
  header?: React.ReactNode;
}

// How far (px) below the strip a section's top may be and still be the
// section in view
const IN_VIEW_SLACK = 12;

// Space (px) between a section and the rule above the next one, on either
// side of the rule
const SECTION_GAP = 32;

// Space (px) between the parts of a section
const PART_GAP = 28;

// Whether a section, or a part of one, has anything to show
export const hasContent = ({ content }: DetailSection): boolean =>
  content !== null && content !== undefined && content !== false;

// The heading of a section or a part, with its count beside it
const Heading: React.FC<{
  id: string;
  text: string;
  count?: number;
  level: "h2" | "h3";
  // The element at the right end of its line, given to what is beneath
  slotRef?: (element: HTMLElement | null) => void;
}> = ({ id, text, count, level, slotRef }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: level === "h2" ? 2 : 1 }}>
    <Typography
      id={id}
      variant={level === "h2" ? "h6" : "subtitle1"}
      component={level}
      sx={
        level === "h2"
          ? { fontWeight: 600, fontSize: "1.3125rem", letterSpacing: "-0.01em" }
          : { fontWeight: 600, fontSize: "0.9375rem" }
      }
    >
      {text}
    </Typography>
    {count !== undefined && (
      <Box
        component="span"
        sx={{
          ...monospaceStyle,
          fontSize: level === "h2" ? "0.875rem" : "0.75rem",
          color: "text.secondary",
        }}
      >
        {count}
      </Box>
    )}
    {slotRef && (
      <Box ref={slotRef} sx={{ ml: "auto", alignSelf: "center", display: "flex" }} />
    )}
  </Box>
);

// The right end of the line of the heading above, where a table puts its
// search rather than on a line of its own; null where there is none
const HeadingSlot = React.createContext<HTMLElement | null>(null);

/** The right end of the heading line above, or null where there is none. */
export const useHeadingSlot = (): HTMLElement | null => useContext(HeadingSlot);

// A section's or a part's heading over its content, the content given the
// right end of the heading's line. Without a heading the content has no
// such place, not even the one of a heading further up.
const Headed: React.FC<{
  id: string;
  text: string;
  count?: number;
  level: "h2" | "h3";
  hidden?: boolean;
  children: React.ReactNode;
}> = ({ id, text, count, level, hidden = false, children }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <>
      {!hidden && <Heading id={id} text={text} count={count} level={level} slotRef={setSlot} />}
      <HeadingSlot.Provider value={hidden ? null : slot}>{children}</HeadingSlot.Provider>
    </>
  );
};

/**
 * The parts of one section of a detail view, one under the other, each
 * under a heading of its own: a section that gathers several kinds of
 * entry, such as a product's claims, specifications and standards. A part
 * with no content is left out; declared as a section is, its id unique in
 * the view.
 */
export const DetailParts: React.FC<{ parts: DetailSection[] }> = ({ parts }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: `${PART_GAP}px` }}>
    {parts.filter(hasContent).map((part) => {
      const headingId = `detail-part-${part.id}`;
      return (
        <Box
          key={part.id}
          component="section"
          aria-labelledby={part.hideHeading ? undefined : headingId}
          aria-label={part.hideHeading ? part.label : undefined}
        >
          <Headed
            id={headingId}
            text={part.heading ?? part.label}
            count={part.count}
            level="h3"
            hidden={part.hideHeading}
          >
            {part.content}
          </Headed>
        </Box>
      );
    })}
  </Box>
);

/**
 * The body of a detail view: a strip of section names that stays in view,
 * with the view's header above it where it gives one, and under it the
 * sections themselves.
 *
 * A view declares its sections as a list; an empty one is left out. The
 * strip marks the section in view while the container scrolls, and a click
 * on a name jumps to its section.
 */
const DetailSections: React.FC<DetailSectionsProps> = ({
  sections,
  scrollContainerRef,
  header,
}) => {
  const shown = useMemo(() => sections.filter(hasContent), [sections]);

  // The height of what stays at the top, the header and the strip: a
  // section is in view once its top reaches the bottom of it, and a jump
  // leaves this much room
  const pinnedRef = useRef<HTMLDivElement>(null);
  const [pinnedHeight, setPinnedHeight] = useState(SECTION_STRIP_HEIGHT);
  useEffect(() => {
    const element = pinnedRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) =>
      setPinnedHeight(entry.target.getBoundingClientRect().height),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const elements = useRef(new Map<string, HTMLElement>());
  const [current, setCurrent] = useState<string | null>(null);
  const currentId =
    current !== null && shown.some(({ id }) => id === current)
      ? current
      : (shown[0]?.id ?? null);

  // Marks the section in view: the last one whose top has reached the strip
  const findCurrent = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || shown.length === 0) {
      return;
    }
    const atEnd =
      container.scrollTop > 0 &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1;
    if (atEnd) {
      setCurrent(shown[shown.length - 1].id);
      return;
    }
    const line =
      container.getBoundingClientRect().top + pinnedHeight + IN_VIEW_SLACK;
    let inView = shown[0].id;
    for (const { id } of shown) {
      const element = elements.current.get(id);
      if (element && element.getBoundingClientRect().top <= line) {
        inView = id;
      }
    }
    setCurrent(inView);
  }, [scrollContainerRef, shown, pinnedHeight]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }
    container.addEventListener("scroll", findCurrent, { passive: true });
    return () => container.removeEventListener("scroll", findCurrent);
  }, [scrollContainerRef, findCurrent]);

  const jumpTo = useCallback((id: string) => {
    setCurrent(id);
    const element = elements.current.get(id);
    // Not every environment scrolls: the test environment does not
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, []);

  if (shown.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        ref={pinnedRef}
        sx={{ position: "sticky", top: 0, zIndex: 5, backgroundColor: "background.paper" }}
      >
        {header}
        <SectionStrip sections={shown} current={currentId} onSelect={jumpTo} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: `${SECTION_GAP}px`,
          px: 2.5,
          pt: 2.5,
          pb: 5,
        }}
      >
        {shown.map((section, index) => {
          const headingId = `detail-section-${section.id}`;
          return (
            <Box
              key={section.id}
              component="section"
              // A section without a heading is named by its label
              aria-labelledby={section.hideHeading ? undefined : headingId}
              aria-label={section.hideHeading ? section.label : undefined}
              ref={(element: HTMLElement | null) => {
                if (element) {
                  elements.current.set(section.id, element);
                } else {
                  elements.current.delete(section.id);
                }
              }}
              sx={{
                scrollMarginTop: `${pinnedHeight + IN_VIEW_SLACK}px`,
                // A rule parts each section from the one before
                ...(index > 0 && {
                  borderTop: 2,
                  borderColor: (theme) => alpha(theme.palette.chart.ink, 0.12),
                  pt: `${SECTION_GAP}px`,
                }),
              }}
            >
              <Headed
                id={headingId}
                text={section.heading ?? section.label}
                count={section.count}
                level="h2"
                hidden={section.hideHeading}
              >
                {section.content}
              </Headed>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default DetailSections;
